/**
 * Service API client for registering verifications with verifieddit.com.
 * All API calls are proxied through the backend to keep the API key server-side.
 */

const API_BASE = typeof window !== 'undefined' && window.location.hostname === 'www.thephenom.app'
  ? 'https://drop.thephenom.app' : ''
const BACKEND_API = `${API_BASE}/api/drop/verifieddit`

interface VerificationParams {
  fileHash: string
  filename: string
  c2paStatus: string
  c2paIssuer?: string
  c2paSignedAt?: string
  claimGenerator?: string
  fileType?: string
  fileSize?: number
  aiDetected?: boolean
  thumbnailUrl?: string
}

interface VerificationResult {
  uuid: string
  shortId: string
  shortUrl: string
}

interface HashCheckResult {
  exists: boolean
  shortUrl?: string
}

/**
 * Check if a file hash has already been submitted to verifieddit.com.
 * Returns { exists: true, shortUrl } if found, { exists: false } otherwise.
 * Non-blocking - returns { exists: false } on any error.
 */
export async function checkExistingHash(
  fileHash: string
): Promise<HashCheckResult> {
  try {
    const res = await fetch(`${BACKEND_API}/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileHash }),
    })
    if (!res.ok) return { exists: false }
    const data = await res.json()
    return {
      exists: !!data.exists,
      shortUrl: data.shortUrl,
    }
  } catch {
    return { exists: false }
  }
}

/**
 * Register a verification with verifieddit.com.
 * Returns { uuid, shortId, shortUrl } on success, null on failure.
 * Non-blocking - the phenom-drop UI works even if this fails.
 */
export async function createVerification(
  params: VerificationParams
): Promise<VerificationResult | null> {
  try {
    const res = await fetch(`${BACKEND_API}/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileHash: params.fileHash,
        filename: params.filename,
        c2paStatus: params.c2paStatus,
        c2paIssuer: params.c2paIssuer,
        c2paSignedAt: params.c2paSignedAt,
        claimGenerator: params.claimGenerator,
        fileType: params.fileType,
        fileSize: params.fileSize,
        aiDetected: params.aiDetected ? 1 : 0,
        thumbnailUrl: params.thumbnailUrl || undefined,
      }),
    })
    if (!res.ok) return null
    const data = await res.json()
    return {
      uuid: data.uuid,
      shortId: data.shortId,
      shortUrl: data.shortUrl || `https://verifieddit.com/verify/${data.shortId}`,
    }
  } catch {
    return null
  }
}
